import { useState } from "react";
import { DINEIN_PAYMENT_METHODS, IDineInBill } from "../models/IDineIn";
import { dineInErrorCode, dineInErrorDescription, getDineInBillByCode, payDineInSession } from "../services/dineInService";

function money(n: number, symbol?: string): string {
  return `${n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${symbol ? ` ${symbol}` : ""}`;
}

interface Props {
  bill: IDineInBill;
  onClose: () => void;
  onPaid: () => void;
  /** Hesap sunucuda değişmişse güncel hâli yukarı verilir */
  onBillRefreshed: (bill: IDineInBill) => void;
}

export default function PayBillModal({ bill, onClose, onPaid, onBillRefreshed }: Props) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [staleTotal, setStaleTotal] = useState<number | null>(null);
  const [paymentMethodId, setPaymentMethodId] = useState<string>("");

  const symbol = bill.session.currencyDisplayText;

  const handlePay = () => {
    if (!paymentMethodId) { setError("Lütfen ödeme yöntemini seçin."); return; }
    setSaving(true);
    setError("");
    setStaleTotal(null);
    payDineInSession(bill.session.orderCode, bill.totalAmount, paymentMethodId)
      .then(() => onPaid())
      .catch((err) => {
        const code = dineInErrorCode(err);
        if (code === "BillTotalChanged") {
          // Hesap bu arada değişti: yeni toplamı çekip kasiyere onaylatıyoruz.
          setStaleTotal(bill.totalAmount);
          setError("Hesap bu sırada değişti. Güncel tutar aşağıda — lütfen kontrol edip tekrar onaylayın.");
          getDineInBillByCode(bill.session.orderCode)
            .then((res) => onBillRefreshed(res.data))
            .catch(() => {});
        } else if (code === "SessionAlreadyPaid") {
          setError("Bu hesabın ödemesi zaten alınmış. Parayı siz almadıysanız kasayla kontrol edin.");
        } else if (code === "SessionHasNoLines") {
          setError("Boş hesap ödenemez. Masayı boşaltabilirsiniz.");
        } else if (code === "PaymentMethodNotAllowed") {
          setError("Salon hesabı yalnızca nakit ya da kart ile kapatılabilir.");
        } else {
          setError(dineInErrorDescription(err) ?? "Ödeme kaydedilemedi.");
        }
      })
      .finally(() => setSaving(false));
  };

  return (
    <div className="dinein-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div className="dinein-modal dinein-modal--narrow">
        <div className="dinein-modal__head">
          <div>
            <div className="dinein-modal__title"><i className="bi bi-cash-coin me-2"></i>Ödeme Al</div>
            <div className="dinein-modal__sub">
              Masa {bill.session.tableNumber}{bill.session.tableLabel ? ` — ${bill.session.tableLabel}` : ""}
            </div>
          </div>
          <button className="dinein-icon-btn" onClick={onClose} disabled={saving}><i className="bi bi-x-lg"></i></button>
        </div>

        <div className="dinein-modal__body">
          <div className="dinein-pay-total">
            <span>Tahsil edilecek</span>
            <strong>{money(bill.totalAmount, symbol)}</strong>
            {staleTotal !== null && staleTotal !== bill.totalAmount && (
              <div className="dinein-pay-total__changed">
                <i className="bi bi-arrow-repeat me-1"></i>
                Önceki tutar {money(staleTotal, symbol)} idi
              </div>
            )}
          </div>

          {/* Ödeme yöntemi — sunucu zorunlu tutuyor */}
          <div className="pay-methods">
            <div className="pay-methods__label">Ödeme Yöntemi <span style={{ color: "#dc2626" }}>*</span></div>
            <div className="pay-methods__options">
              {DINEIN_PAYMENT_METHODS.map((m) => (
                <button key={m.id} type="button" disabled={saving}
                  className={`pay-method${paymentMethodId === m.id ? " pay-method--active" : ""}`}
                  onClick={() => { setPaymentMethodId(m.id); setError(""); }}>
                  <i className={`bi ${m.icon}`}></i>
                  <span>{m.label}</span>
                  {paymentMethodId === m.id && <i className="bi bi-check-circle-fill pay-method__check"></i>}
                </button>
              ))}
            </div>
          </div>

          <div className="dinein-pay-lines">
            {bill.lines.map((l) => (
              <div key={l.lineId}>
                <span>{l.quantity}× {l.foodNameTr}</span>
                <span>{money(l.lineTotal, symbol)}</span>
              </div>
            ))}
          </div>

          {error && (
            <div className="dinein-alert dinein-alert--error"><i className="bi bi-exclamation-circle-fill"></i><span>{error}</span></div>
          )}

          <div className="dinein-hint">
            <i className="bi bi-info-circle me-1"></i>
            Bu sistem para tahsil etmez; tutarı kasadan/POS'tan aldıktan sonra onaylayın. Ödeme sonrası masa boşa düşer.
          </div>
        </div>

        <div className="dinein-modal__foot">
          <button className="dinein-btn dinein-btn--ghost" onClick={onClose} disabled={saving}>Vazgeç</button>
          <button className="dinein-btn dinein-btn--success" onClick={handlePay} disabled={saving || bill.lines.length === 0 || !paymentMethodId}>
            {saving
              ? <><span className="spinner-border spinner-border-sm me-1" />Kaydediliyor...</>
              : <><i className="bi bi-check-lg me-1"></i>Ödendi ({money(bill.totalAmount, symbol)})</>}
          </button>
        </div>
      </div>
    </div>
  );
}
